import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  createRetentionPolicy,
  getRetentionPolicies,
  updateRetentionPolicy,
} from '../api/dataLifecycleApi';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { useAuth } from '../auth/AuthContext';
import { hasPermission } from '../auth/permissions';
import { DataTable } from '../components/DataTable';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { StatusBadge } from '../components/StatusBadge';
import { TableToolbar } from '../components/TableToolbar';
import { Button } from '../components/ui/Button';
import { TextInput } from '../components/ui/TextInput';

const { semantic } = colorTokens;

const emptyForm = {
  key: '',
  name: '',
  description: '',
  entity_type: 'customers',
  retention_days: 365,
  purge_delay_days: 30,
  legal_hold_supported: true,
  is_active: true,
};

const rowActionSx = {
  minHeight: 32,
  px: 1,
};

const dialogPaperSx = {
  width: 'min(640px, calc(100vw - 48px))',
  maxWidth: 'min(640px, calc(100vw - 48px))',
  maxHeight: 'calc(100vh - 64px)',
  bgcolor: semantic.bg.surface,
  backgroundImage: 'none',
  border: `1px solid ${semantic.border.subtle}`,
  borderRadius: `${radiusTokens.card}px`,
};

/** ON: cherry track + white thumb; OFF: grey track + white/light thumb. */
const retentionSwitchSx = {
  width: 42,
  height: 24,
  p: 0,
  '& .MuiSwitch-switchBase': {
    p: 0,
    m: '2px',
    color: '#ffffff',
    transitionDuration: '200ms',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#ffffff',
      '& + .MuiSwitch-track': {
        bgcolor: colorTokens.brand.primary,
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled': {
        color: '#ffffff',
        '& + .MuiSwitch-track': {
          bgcolor: colorTokens.brand.primary,
          opacity: 0.4,
        },
      },
    },
    '&.Mui-disabled': {
      color: semantic.text.muted,
      '& + .MuiSwitch-track': {
        bgcolor: semantic.border.default,
        opacity: 0.4,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 20,
    height: 20,
    bgcolor: '#ffffff',
    boxShadow: 'none',
  },
  '& .MuiSwitch-track': {
    borderRadius: 12,
    bgcolor: semantic.border.default,
    opacity: 1,
  },
};

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: '18px',
  color: semantic.text.primary,
  minWidth: 0,
  maxWidth: '100%',
  overflowWrap: 'anywhere',
  wordBreak: 'break-word',
};

export function RetentionPoliciesPage() {
  const { t } = useTranslation(['dataLifecycle', 'common']);
  const { session } = useAuth();
  const canManage = hasPermission(session, 'system.data_lifecycle.manage');

  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  const emptyValue = t('common:empty_value');
  const recordCount = policies.length;

  const loadPolicies = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getRetentionPolicies({ per_page: 100 });
      setPolicies(response.data || []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = useCallback((policy) => {
    setEditing(policy);
    setForm({
      key: policy.key,
      name: policy.name,
      description: policy.description || '',
      entity_type: policy.entity_type,
      retention_days: policy.retention_days,
      purge_delay_days: policy.purge_delay_days,
      legal_hold_supported: policy.legal_hold_supported,
      is_active: policy.is_active,
    });
    setDialogOpen(true);
  }, []);

  const handleSave = async () => {
    setMessage('');
    setSaving(true);

    try {
      if (editing) {
        await updateRetentionPolicy(editing.id, form);
        setMessage(t('dataLifecycle:retention.policy_updated'));
      } else {
        await createRetentionPolicy(form);
        setMessage(t('dataLifecycle:retention.policy_created'));
      }

      setDialogOpen(false);
      loadPolicies();
    } catch (err) {
      setMessage(err?.response?.data?.message || err.message || t('dataLifecycle:retention.save_failed'));
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(() => [
    {
      id: 'key',
      label: t('common:key'),
      render: (row) => (
        row.key
          ? <Typography sx={monoSx}>{row.key}</Typography>
          : emptyValue
      ),
    },
    {
      id: 'name',
      label: t('common:name'),
      render: (row) => row.name || emptyValue,
    },
    {
      id: 'entity_type',
      label: t('dataLifecycle:recycle_bin.columns.entity'),
      render: (row) => (
        row.entity_type
          ? (
            <Typography sx={monoSx}>
              {t(`dataLifecycle:entity_types.${row.entity_type}`, { defaultValue: row.entity_type })}
            </Typography>
          )
          : emptyValue
      ),
    },
    {
      id: 'retention_days',
      label: t('dataLifecycle:retention.columns.retention_days'),
      render: (row) => (row.retention_days != null ? row.retention_days : emptyValue),
    },
    {
      id: 'purge_delay_days',
      label: t('dataLifecycle:retention.columns.purge_delay'),
      render: (row) => (row.purge_delay_days != null ? row.purge_delay_days : emptyValue),
    },
    {
      id: 'is_active',
      label: t('common:active_label'),
      render: (row) => (
        <StatusBadge
          status={row.is_active ? 'active' : 'inactive'}
          category="generic"
          apiLabel={row.is_active
            ? t('dataLifecycle:retention.active')
            : t('dataLifecycle:retention.inactive')}
          size="Sm"
          tone={row.is_active ? 'Success' : 'Neutral'}
        />
      ),
    },
    {
      id: 'actions',
      label: t('common:actions'),
      align: 'right',
      render: (row) => (
        canManage
          ? (
            <Button variant="Ghost" onClick={() => openEdit(row)} sx={rowActionSx}>
              {t('common:edit')}
            </Button>
          )
          : emptyValue
      ),
    },
  ], [canManage, emptyValue, openEdit, t]);

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      <PageHeader
        type="List"
        title={t('dataLifecycle:retention.title')}
        subtitle={t('dataLifecycle:retention.subtitle')}
        actions={canManage ? (
          <Button variant="Accent" onClick={openCreate}>
            {t('dataLifecycle:retention.create_policy')}
          </Button>
        ) : null}
      />

      <Alert severity="info" sx={{ mb: 2 }}>
        {t('dataLifecycle:retentionPoliciesHelp')}
      </Alert>

      {message && (
        <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMessage('')}>
          {message}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={(
            <Button variant="Ghost" onClick={loadPolicies} sx={{ minHeight: 32 }}>
              {t('common:retry')}
            </Button>
          )}
        >
          {t('dataLifecycle:retention.load_failed')}
        </Alert>
      )}

      <SectionCard variant="Default" noPadding sx={{ width: '100%' }}>
        <TableToolbar
          mode="Default"
          title={t('dataLifecycle:retention.policies')}
          meta={(
            <StatusBadge
              status="list"
              category="generic"
              apiLabel={t('dataLifecycle:retention.toolbar_count', { count: recordCount })}
              size="Sm"
              tone="Neutral"
            />
          )}
        />

        {loading ? (
          <LoadingSkeleton
            type="Table"
            embedded
            rows={3}
            columns={columns.length}
          />
        ) : !error ? (
          <DataTable
            type="Default"
            embedded
            columns={columns}
            rows={policies}
            getRowKey={(row) => row.id}
            emptyMessage={t('common:no_data_found')}
            sx={{
              overflowX: 'auto',
              width: '100%',
              '& table': {
                width: '100%',
                minWidth: '100%',
              },
            }}
          />
        ) : null}
      </SectionCard>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          if (!saving) setDialogOpen(false);
        }}
        maxWidth={false}
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 3,
            fontFamily: typographyTokens.fontFamily,
            fontWeight: typographyTokens.fontWeightSemibold,
            fontSize: 16,
            color: semantic.text.primary,
            borderBottom: `1px solid ${semantic.border.subtle}`,
          }}
        >
          {editing
            ? t('dataLifecycle:retention.edit_policy')
            : t('dataLifecycle:retention.create_policy_dialog')}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 2.5, pb: 1, overflowY: 'auto' }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextInput
              density="Compact"
              label={t('common:key')}
              value={form.key}
              disabled={Boolean(editing) || saving}
              onChange={(e) => setForm((p) => ({ ...p, key: e.target.value }))}
            />
            <TextInput
              density="Compact"
              label={t('common:name')}
              value={form.name}
              disabled={saving}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
            <TextInput
              density="Compact"
              label={t('dataLifecycle:recycle_bin.entity_type')}
              value={form.entity_type}
              disabled={saving}
              onChange={(e) => setForm((p) => ({ ...p, entity_type: e.target.value }))}
            />
            <TextInput
              density="Compact"
              type="number"
              label={t('dataLifecycle:retention.retention_days')}
              value={form.retention_days}
              disabled={saving}
              onChange={(e) => setForm((p) => ({ ...p, retention_days: Number(e.target.value) }))}
            />
            <TextInput
              density="Compact"
              type="number"
              label={t('dataLifecycle:retention.purge_delay_days')}
              value={form.purge_delay_days}
              disabled={saving}
              onChange={(e) => setForm((p) => ({ ...p, purge_delay_days: Number(e.target.value) }))}
            />
            <TextInput
              density="Compact"
              label={t('common:description')}
              value={form.description}
              disabled={saving}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              multiline
              minRows={2}
            />
            <FormControlLabel
              sx={{
                ml: 0,
                mr: 0,
                gap: 1.5,
                '& .MuiFormControlLabel-label': {
                  fontFamily: typographyTokens.fontFamily,
                  fontSize: 13,
                  color: semantic.text.secondary,
                },
              }}
              control={(
                <Switch
                  checked={form.legal_hold_supported}
                  disabled={saving}
                  onChange={(e) => setForm((p) => ({ ...p, legal_hold_supported: e.target.checked }))}
                  sx={retentionSwitchSx}
                />
              )}
              label={t('dataLifecycle:retention.legal_hold_supported')}
            />
            <FormControlLabel
              sx={{
                ml: 0,
                mr: 0,
                gap: 1.5,
                '& .MuiFormControlLabel-label': {
                  fontFamily: typographyTokens.fontFamily,
                  fontSize: 13,
                  color: semantic.text.secondary,
                },
              }}
              control={(
                <Switch
                  checked={form.is_active}
                  disabled={saving}
                  onChange={(e) => setForm((p) => ({ ...p, is_active: e.target.checked }))}
                  sx={retentionSwitchSx}
                />
              )}
              label={t('common:active_label')}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, justifyContent: 'flex-end', gap: 1 }}>
          <Button
            variant="Ghost"
            disabled={saving}
            onClick={() => setDialogOpen(false)}
          >
            {t('common:cancel')}
          </Button>
          <Button
            variant="Accent"
            loading={saving}
            disabled={saving}
            onClick={handleSave}
          >
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
